import { Modal, Form, Select, InputNumber, Input, Alert, message } from "antd";
import { useCreate } from "@refinedev/core";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import type { AuditFormValues } from "../../../types/audit";

interface AuditCreateModalProps {
  visible: boolean;
  onClose: () => void;
  planningId: number;
  seasonId: number;
  /**
   * Ya no se usa para filtrar el listado (el endpoint
   * `assignable-farms` está scopeado por la planning), se conserva
   * como prop por compatibilidad con los call-sites existentes.
   */
  countryId?: number;
  onSuccess?: () => void;
}

interface CrossAssignment {
  planning_group_id: number;
  planning_group_name: string;
  audit_company_id: number | null;
  audit_company_name: string | null;
}

interface AssignableFarm {
  id: number;
  name: string;
  producer_name: string | null;
  province_name: string | null;
  town_name: string | null;
  already_in_planning: boolean;
  /** Planning_groups de OTRAS empresas de auditoría en la misma
   *  campaña donde esta finca ya tiene audit. Vacío si no hay
   *  conflicto cruzado. */
  cross_assignments?: CrossAssignment[];
}

/**
 * AuditCreateModal Component
 * Modal for creating a new audit within a planning group.
 *
 * Data source: `/planning-groups/{planningId}/assignable-farms/`,
 * el mismo que usa la pantalla de asignación masiva. Filtra por país
 * de la planning y excluye fincas ya en OTRAS planificaciones de la
 * misma campaña, y expone `already_in_planning` para marcar las que
 * ya están en ESTA planificación. Antes tirábamos de
 * `/farms/?country=X&page_size=10000` — devolvía respuestas gigantes
 * y quedaba dependiendo del country prop; cuando la planning venía
 * sin country resuelto el dropdown salía vacío ("el buscador no
 * funciona"). Con este endpoint el listado sale correcto sin depender
 * de props laterales.
 */
export const AuditCreateModal = ({
  visible,
  onClose,
  planningId,
  seasonId,
  // countryId — no leído, ver comentario en la interfaz.
  onSuccess,
}: AuditCreateModalProps) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<AuditFormValues>();
  const { mutate: createAudit } = useCreate();
  const [farms, setFarms] = useState<AssignableFarm[]>([]);
  const [loadingFarms, setLoadingFarms] = useState(false);
  const [auditTypes, setAuditTypes] = useState<any[]>([]);
  const [loadingAuditTypes, setLoadingAuditTypes] = useState(false);
  // Finca seleccionada actualmente — permite mostrar un aviso visible
  // cuando el user elige una finca que ya tiene otra auditoría en esta
  // misma planificación (antes se bloqueaba con `disabled`, ahora se
  // permite crear duplicados intencionadamente).
  const [selectedFarmId, setSelectedFarmId] = useState<number | null>(null);
  const selectedFarm = selectedFarmId != null
    ? farms.find((f) => f.id === selectedFarmId) ?? null
    : null;
  const selectedFarmAlreadyInPlanning = Boolean(
    selectedFarm?.already_in_planning,
  );
  const selectedFarmCrossAssignments =
    selectedFarm?.cross_assignments ?? [];

  // Load assignable farms when modal opens.
  useEffect(() => {
    const loadFarms = async () => {
      if (!visible || !planningId) return;

      setLoadingFarms(true);
      try {
        const { apiClient } = await import('../../../services/api/apiClient');
        const response = await apiClient.get<AssignableFarm[]>(
          `/planning-groups/${planningId}/assignable-farms/`,
        );
        setFarms(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error('❌ Error loading farms:', error);
        message.error('Error al cargar las fincas');
        setFarms([]);
      } finally {
        setLoadingFarms(false);
      }
    };

    loadFarms();
  }, [visible, planningId]);

  // Load audit types when modal opens
  useEffect(() => {
    const loadAuditTypes = async () => {
      if (!visible) return;
      
      setLoadingAuditTypes(true);
      try {
        const { apiClient } = await import('../../../services/api/apiClient');
        const response = await apiClient.get('/audit-types/?page_size=100');
        
        console.log('📋 Audit types loaded:', {
          count: response.data.count,
          resultsLength: response.data.results?.length,
        });
        
        if (response.data.results) {
          setAuditTypes(response.data.results);
        } else if (Array.isArray(response.data)) {
          setAuditTypes(response.data);
        }
      } catch (error) {
        console.error('❌ Error loading audit types:', error);
        message.error('Error al cargar los tipos de auditoría');
      } finally {
        setLoadingAuditTypes(false);
      }
    };

    loadAuditTypes();
  }, [visible]);

  const handleSubmit = (values: AuditFormValues) => {
    createAudit(
      {
        resource: "audits",
        values: {
          ...values,
          planning_group: planningId,
          season: seasonId,
          audit_state: 1, // Planned state
          type_of_audit: "complete",
          status_of_audit: "planned",
        },
      },
      {
        onSuccess: () => {
          form.resetFields();
          setSelectedFarmId(null);
          onClose();
          if (onSuccess) {
            onSuccess();
          }
        },
      }
    );
  };

  const handleCancel = () => {
    form.resetFields();
    setSelectedFarmId(null);
    onClose();
  };

  return (
    <Modal
      title={t("audit.create")}
      open={visible}
      onCancel={handleCancel}
      onOk={() => form.submit()}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      width={600}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        autoComplete="off"
      >
        <Form.Item
          name="farm"
          label={t("audit.farm")}
          rules={[
            {
              required: true,
              message: `${t("audit.farm")} ${t("common.required")}`,
            },
          ]}
        >
          <Select
            placeholder={t(
              "audit.farm_search_placeholder",
              "Buscar por productor o finca…",
            )}
            showSearch
            allowClear
            loading={loadingFarms}
            getPopupContainer={(trigger) => trigger.parentElement || document.body}
            // El search se aplica sobre `producer_name + name + town`
            // combinados en `optionSearchText`. Sin acentos y en
            // minúsculas para que "OLIVER" y "olivér" hagan match.
            optionFilterProp="label"
            filterOption={(input, option) => {
              const hay = (option?.title as string | undefined) ?? "";
              const needle = input
                .toLowerCase()
                .normalize("NFD")
                .replace(/\p{Diacritic}/gu, "");
              return hay.includes(needle);
            }}
            onChange={(value) => setSelectedFarmId(value ?? null)}
            options={farms.map((farm) => {
              const producer = farm.producer_name || "—";
              const town = farm.town_name || "";
              const province = farm.province_name || "";
              const location = town && province ? `${town} (${province})` : town || province;
              const label = `${producer} · ${farm.name}${location ? ` — ${location}` : ""}`;
              // `title` almacena el texto normalizado que usa el filter
              // — así `filterOption` no repite el trabajo por cada
              // pulsación.
              const searchText = `${producer} ${farm.name} ${town} ${province}`
                .toLowerCase()
                .normalize("NFD")
                .replace(/\p{Diacritic}/gu, "");
              // Las fincas ya en esta planning o auditadas por otra
              // empresa se pueden seleccionar — a veces un mismo predio
              // necesita más de un audit (reauditar por incidencia, o
              // dos empresas auditoras en la misma campaña). Sólo se
              // pintan con opacidad y una nota; el Alert de abajo
              // recuerda al operador qué conflicto existe.
              const crossAssignments = farm.cross_assignments ?? [];
              const hasCrossAssignment = crossAssignments.length > 0;
              let suffix: React.ReactNode = null;
              if (farm.already_in_planning) {
                suffix = (
                  <em style={{ color: "#faad14" }}>· ya en esta planning</em>
                );
              } else if (hasCrossAssignment) {
                const names = crossAssignments
                  .map((c) => c.audit_company_name || "otra empresa")
                  .join(", ");
                suffix = (
                  <em style={{ color: "#1677ff" }}>
                    · ya asignada a {names}
                  </em>
                );
              }
              return {
                value: farm.id,
                label: suffix ? (
                  <span style={{ opacity: 0.6 }}>
                    {label} {suffix}
                  </span>
                ) : (
                  label
                ),
                title: searchText,
              };
            })}
          />
        </Form.Item>

        {selectedFarmAlreadyInPlanning && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message={t(
              "audit.create.farm_already_in_planning_title",
              "Esta finca ya tiene una auditoría en la planificación",
            )}
            description={t(
              "audit.create.farm_already_in_planning_desc",
              "Se creará una auditoría adicional para {{farm}}. Revisa que la duplicidad es intencionada (por ejemplo, reauditar por incidencia).",
              { farm: selectedFarm?.name ?? "" },
            )}
          />
        )}

        {!selectedFarmAlreadyInPlanning &&
          selectedFarmCrossAssignments.length > 0 && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message={t(
                "audit.create.farm_cross_assignment_title",
                "Esta finca ya la audita otra empresa esta campaña",
              )}
              description={
                <>
                  {t(
                    "audit.create.farm_cross_assignment_desc",
                    "Puedes crear la auditoría igualmente. Asignaciones existentes:",
                  )}
                  <ul style={{ margin: "4px 0 0 0", paddingLeft: 18 }}>
                    {selectedFarmCrossAssignments.map((c) => (
                      <li key={c.planning_group_id}>
                        <strong>
                          {c.audit_company_name ?? t(
                            "audit.create.farm_cross_assignment_no_company",
                            "Empresa sin asignar",
                          )}
                        </strong>
                        {" — "}
                        {c.planning_group_name}
                      </li>
                    ))}
                  </ul>
                </>
              }
            />
          )}

        <Form.Item
          name="audit_type"
          label={t("audit.type")}
          rules={[
            {
              required: true,
              message: `${t("audit.type")} ${t("common.required")}`,
            },
          ]}
        >
          <Select
            loading={loadingAuditTypes}
            placeholder={`${t("common.select")} ${t("audit.type")}`}
            getPopupContainer={(trigger) => trigger.parentElement || document.body}
            options={auditTypes.map((type) => ({
              label: type.name,
              value: type.id,
            }))}
          />
        </Form.Item>

        <Form.Item
          name="samples_to_take"
          label={t("audit.samples_to_take")}
          rules={[
            {
              required: true,
              message: `${t("audit.samples_to_take")} ${t("common.required")}`,
            },
          ]}
          initialValue={0}
        >
          <InputNumber
            min={0}
            style={{ width: "100%" }}
            placeholder={t("audit.samples_to_take")}
          />
        </Form.Item>

        <Form.Item
          name="samples_to_process"
          label={t("audit.samples_to_process")}
          rules={[
            {
              required: true,
              message: `${t("audit.samples_to_process")} ${t("common.required")}`,
            },
          ]}
          initialValue={0}
        >
          <InputNumber
            min={0}
            style={{ width: "100%" }}
            placeholder={t("audit.samples_to_process")}
          />
        </Form.Item>

        <Form.Item name="remark" label={t("audit.remark")}>
          <Input.TextArea
            rows={4}
            placeholder={t("audit.remark_placeholder")}
            maxLength={500}
            showCount
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
